import React from 'react';
import { Box, Typography, Chip, Paper, LinearProgress } from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';

export default function CompetenciesDisplay({ data }) {
  if (!data) return null;

  // Handle different possible structures
  const competencies = data.competencies || [];
  const skills = data.skills || [];
  const knowledge = data.knowledge || [];
  const proficiencyLevels = data.proficiency_levels || {};
  const isParsedMarkdown = data.format === 'parsed_markdown';
  
  // Group competencies by area for better display
  const competenciesByArea = {};
  if (competencies.length > 0) {
    competencies.forEach(comp => {
      const area = comp.area || 'Other';
      if (!competenciesByArea[area]) {
        competenciesByArea[area] = [];
      }
      competenciesByArea[area].push(comp);
    });
  }

  // Helper to render proficiency level
  const renderProficiency = (level) => {
    const levels = {
      'beginner': { value: 25, color: '#fbbf24' },
      'intermediate': { value: 50, color: '#3b82f6' },
      'advanced': { value: 75, color: '#8b5cf6' },
      'expert': { value: 100, color: '#10b981' }
    };
    
    const levelData = levels[level?.toLowerCase()] || { value: 50, color: '#94a3b8' };
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
        <Typography variant="caption" sx={{ minWidth: 100, fontWeight: 600 }}>
          {level || 'Not specified'}
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={levelData.value} 
          sx={{ 
            flex: 1, 
            height: 8, 
            borderRadius: 4,
            '& .MuiLinearProgress-bar': { backgroundColor: levelData.color }
          }} 
        />
      </Box>
    );
  };

  return (
    <Box>
      {/* Competencies Grouped by Area */}
      {Object.keys(competenciesByArea).length > 0 && (
        <Box sx={{ mb: 3 }}>
          {Object.entries(competenciesByArea).map(([area, areaComps]) => (
            <Box key={area} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmojiObjectsIcon /> {area} ({areaComps.length})
              </Typography>
              {areaComps.map((comp, idx) => (
                <Paper key={idx} sx={{ p: 2.5, mb: 2, border: '2px solid #e2e8f0', bgcolor: '#fafafa' }}>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      {comp.name || comp.title || `Competency ${idx + 1}`}
                    </Typography>
                    {comp.cluster && (
                      <Chip label={comp.cluster} size="small" color="secondary" sx={{ mt: 0.5 }} />
                    )}
                  </Box>
                  
                  {comp.evidence && (
                    <Box sx={{ mb: 1.5, p: 1.5, bgcolor: '#f0f9ff', borderRadius: 1, borderLeft: '3px solid #3b82f6' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', display: 'block', mb: 0.5 }}>
                        Evidence:
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1e3a8a', fontSize: '0.875rem' }}>
                        {comp.evidence}
                      </Typography>
                    </Box>
                  )}
                  
                  {comp.description && !comp.evidence && (
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
                      {comp.description}
                    </Typography>
                  )}
                  
                  {(comp.intensity || comp.level) && (
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 0.5 }}>
                        Development Intensity: {comp.level || 'Not specified'}
                      </Typography>
                      {comp.intensity && (
                        <LinearProgress 
                          variant="determinate" 
                          value={comp.intensity} 
                          sx={{ 
                            height: 10, 
                            borderRadius: 5,
                            bgcolor: '#e2e8f0',
                            '& .MuiLinearProgress-bar': { 
                              bgcolor: comp.intensity >= 70 ? '#10b981' : comp.intensity >= 50 ? '#3b82f6' : '#fbbf24'
                            }
                          }} 
                        />
                      )}
                    </Box>
                  )}
                  
                  {comp.proficiency_level && renderProficiency(comp.proficiency_level)}
                  
                  {comp.skills && comp.skills.length > 0 && (
                    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {comp.skills.map((skill, skillIdx) => (
                        <Chip 
                          key={skillIdx} 
                          label={typeof skill === 'string' ? skill : skill.name} 
                          size="small" 
                          variant="outlined"
                          color="primary"
                        />
                      ))}
                    </Box>
                  )}
                </Paper>
              ))}
            </Box>
          ))}
        </Box>
      )}

      {/* Skills Section */}
      {skills.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <WorkIcon sx={{ color: '#0f172a' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Skills ({skills.length})
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {skills.map((skill, idx) => (
              <Chip 
                key={idx} 
                label={typeof skill === 'string' ? skill : skill.name || skill.title} 
                color="primary"
                variant="filled"
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Knowledge Areas */}
      {knowledge.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SchoolIcon sx={{ color: '#0f172a' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Knowledge Areas ({knowledge.length})
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {knowledge.map((item, idx) => (
              <Chip 
                key={idx} 
                label={typeof item === 'string' ? item : item.area || item.title} 
                color="secondary"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Proficiency Levels Overview */}
      {Object.keys(proficiencyLevels).length > 0 && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#fefce8', borderRadius: 2, border: '1px solid #fbbf24' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <EmojiObjectsIcon sx={{ color: '#92400e' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#92400e' }}>
              Proficiency Levels
            </Typography>
          </Box>
          {Object.entries(proficiencyLevels).map(([key, level]) => (
            <Box key={key} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {key}:
              </Typography>
              {renderProficiency(level)}
            </Box>
          ))}
        </Box>
      )}

      {/* Fallback: Show markdown or raw data if structure doesn't match */}
      {competencies.length === 0 && skills.length === 0 && knowledge.length === 0 && (
        <Box>
          {data.raw_markdown ? (
            <Box sx={{ p: 3, bgcolor: '#fffbeb', borderRadius: 2, border: '2px solid #f59e0b' }}>
              <Typography variant="h6" sx={{ color: '#92400e', mb: 2, fontWeight: 700 }}>
                ⚠️ Competencies Analysis (Markdown Format)
              </Typography>
              <Typography variant="caption" sx={{ color: '#78350f', mb: 2, display: 'block' }}>
                The AI returned a markdown analysis. We're working on parsing this into a visual format.
              </Typography>
              <Box sx={{ 
                p: 2, 
                bgcolor: '#fff', 
                borderRadius: 1, 
                maxHeight: '500px', 
                overflowY: 'auto',
                '& h3, & h4': { color: '#0f172a', fontWeight: 700, mt: 2, mb: 1 },
                '& p': { color: '#475569', mb: 1 },
                '& ul, & ol': { pl: 3, color: '#64748b' },
                '& strong': { color: '#1e293b', fontWeight: 600 }
              }}>
                <div dangerouslySetInnerHTML={{ 
                  __html: data.raw_markdown
                    .replace(/###\s+(.+)/g, '<h3>$1</h3>')
                    .replace(/####\s+(.+)/g, '<h4>$1</h4>')
                    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                    .replace(/\n\n/g, '<p></p>')
                    .replace(/- (.+)/g, '<li>$1</li>')
                }} />
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
              <Typography variant="caption" sx={{ color: '#64748b', mb: 1, display: 'block' }}>
                Raw Competencies Data:
              </Typography>
              <pre style={{ 
                margin: 0, 
                whiteSpace: 'pre-wrap', 
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                lineHeight: 1.4
              }}>
                {JSON.stringify(data, null, 2)}
              </pre>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

